import * as React from 'react';

import { ThemeProvider, css } from '@emotion/react';
import { Tooltip, styled } from '@mui/material';
import { type WrappedFieldProps } from 'redux-form';

import FormError from '~/components/FormError/FormError';
import Label, { type LabelPosition, type LabelProps } from '~/components/Label/Label';
import { FlexContainer } from '~/components/Layout';
import { GreyLink } from '~/components/Link';
import { InputSize, type HeightSize } from '~/constants/form';
import { colors, fontFamily, fontWeight, formSizes } from '~/styles/constants';
import { generateRandomInputId } from '~/utils/utils';

const LabelWithUrl = styled('div')`
  display: flex;
  justify-content: space-between;
`;

interface Theme {
  backgroundColor: string;
  borderColor: string;
  height: string;
  responsive: boolean;
  width: number;
}

const FormFieldFocusedMessage = styled('div')`
  color: ${colors.gray700};
  font-weight: ${fontWeight.normal};
  font-size: 12px;
  line-height: 18px;
  margin-top: 8px;
`;

interface MaskedTextProps {
  disabled?: boolean;
  onClick?: () => void;
  tabIndex?: number;
}

const StyledMaskedText = styled('div')<MaskedTextProps>`
  cursor: ${(props) => (props.disabled ? 'default' : 'pointer')};
  color: ${(props) =>
    props.disabled ? colors.form.disabledLabelFontColor : colors.form.fontColor};
`;

const StyledInput = styled('input')<{ theme?: Theme }>`
  height: ${(props) => (props.theme.height ? `${props.theme.height}px` : formSizes.inputHeight)};
  line-height: ${(props) =>
    props.theme.height ? `${props.theme.height}px` : formSizes.inputHeight};
  border-radius: 3px;
  border: 1px solid ${(props) => props.theme.borderColor};
  font-size: 14px;
  font-family: ${fontFamily};
  color: ${colors.form.fontColor};
  padding: 0px 10px;
  outline: none;
  width: ${(props) => (props.theme.responsive ? '100%' : `${props.theme.width}px`)};
  box-sizing: border-box;
  background-color: ${(props) => props.theme.backgroundColor};
  -webkit-appearance: none;

  ::placeholder {
    color: ${colors.form.placeholderFontColor};
    opacity: 1; /* Firefox */
  }

  ::-ms-input-placeholder {
    color: ${colors.form.placeholderFontColor};
  }

  :-ms-input-placeholder {
    color: ${colors.form.placeholderFontColor};
  }

  :focus,
  :active {
    border-color: ${colors.form.activeBorderColor};
  }

  :disabled {
    background-color: ${colors.form.greyDisabled};
    color: ${colors.form.disabledLabelFontColor};
    cursor: not-allowed;
  }
`;

const inputNumberStyle = css`
  input[type='number'] {
    -moz-appearance: textfield;
    appearance: textfield;
    margin: 0;
  }
  input[type='number']::-webkit-inner-spin-button,
  input[type='number']::-webkit-outer-spin-button {
    -moz-appearance: none;
    -webkit-appearance: none;
    appearance: none;
    margin: 0;
    opacity: 0;
  }
`;

const InputContainer = styled('div')<{ type?: string }>`
  ${(props) => (props.type === 'number' ? inputNumberStyle : '')};
`;

const InputWithLeftMargin = styled(StyledInput)`
  margin-left: 10px;
`;

const ErrorMessage = styled('div')<FormErrorProps>`
  font-size: 12px;
  color: ${colors.dangerRed};
  margin-top: 5px;
  ${({ customStyle }) => customStyle};
`;

const InfoMessage = styled('p')`
  font-weight: ${fontWeight.normal};
  font-size: 0.75rem;
  color: ${colors.gray800};
  margin: 4px 0 0 0;
  text-align: left;
`;

const MessageContainer = styled('div')`
  height: 1.25rem;
  margin-top: 0.25rem;
`;

const FormErrorWithMarginTop = styled(FormError)<FormErrorWithMarginTopProps>`
  margin-top: 5px;
  color: ${colors.dangerRed};
  ${({ customStyle }) => customStyle};
`;

const inputStepArrowStyles = css`
  background-color: #f2f6f7;
  width: 16px;
  cursor: pointer;
  margin-left: -18px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: #718087;
  font-size: 10px;
`;

const StepsContainer = styled('div')`
  display: flex;
  flex-direction: column;
`;

const StepUp = styled('div')<{ theme?: Theme }>`
  ${inputStepArrowStyles};
  box-sizing: border-box;
  height: calc(50% - 1px);
  margin-top: 1px;
  min-height: 15px;
  border-top-right-radius: 2px;
  border-left: 1px solid ${(props) => props.theme.borderColor};
  border-bottom: 0.5px solid ${(props) => props.theme.borderColor};
`;

const StepDown = styled('div')<{ theme?: Theme }>`
  ${inputStepArrowStyles};
  box-sizing: border-box;
  height: calc(50% - 1px);
  margin-top: -1px;
  min-height: 16px;
  border-bottom-right-radius: 2px;
  border-left: 1px solid ${(props) => props.theme.borderColor};
  border-top: 0.5px solid ${(props) => props.theme.borderColor};
`;

type FormErrorProps = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  customStyle?: any;
};

type FormErrorWithMarginTopProps = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  customStyle?: any;
};

interface InputProps {
  autoComplete?: string;
  autofocus?: boolean;
  autoselect?: boolean;
  className?: string;
  dataAnalytics?: string;
  dataE2E?: string;
  dataPendo?: string;
  dataTestid?: string;
  decimalPrecision?: number; // number of decimal points to display for numeric inputs, default 2
  disabled?: boolean;
  errorArea?: boolean;
  errorMessage?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  errorMessageStyle?: any;
  height?: HeightSize;
  id?: string;
  infoMessage?: string;
  inputMode?:
    | 'decimal'
    | 'email'
    | 'none'
    | 'numeric'
    | 'search'
    | 'tel'
    | 'text'
    | 'url'
    | undefined;
  inputRef?: ((elem: HTMLInputElement) => void) | React.MutableRefObject<HTMLInputElement>;
  invalid?: boolean;
  label?: string;
  labelPosition?: LabelPosition;
  labelProps?: LabelProps;
  linkText?: string;
  linkUrl?: string;
  max?: number;
  min?: number;
  name?: string;
  onBlur?: React.ChangeEventHandler<HTMLInputElement>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange?(value: any): void;
  onEnter?: React.ChangeEventHandler<HTMLInputElement>;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  pasteEdited?: boolean;
  placeholder?: string;
  readOnly?: boolean;
  responsive?: boolean;
  size?: InputSize;
  step?: number;
  tabIndex?: number;
  type?: string;
  value?: string;
  wrapperClassName?: string;
}

class Input extends React.Component<InputProps> {
  static defaultProps: Partial<InputProps> = {
    labelPosition: 'top',
    size: InputSize.medium,
  };
  blur = () => {
    this.input.blur();
  };

  focus = () => {
    this.input.focus();
  };

  handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { onChange } = this.props;

    if (onChange) onChange(event.target.value);
  };

  id = '';

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  input: any = null;

  renderNumberInputSteps = () => {
    const handleStepClick = (direction: 'down' | 'up') => () => {
      const { onChange } = this.props;
      if (!this.props.disabled && onChange)
        onChange(newNumericInputValue(Number(this.input.value), direction));
    };

    const newNumericInputValue = (oldValue: number, direction: 'down' | 'up'): number => {
      const { decimalPrecision, max, min, step } = this.props;
      const stepValue = Number(step) || 1;
      let newValue;
      if (direction === 'up') {
        // don't increment above max
        newValue = max && oldValue + stepValue >= max ? oldValue : oldValue + stepValue;
      } else {
        // don't decrement below min
        newValue = min && oldValue - stepValue <= min ? oldValue : oldValue - stepValue;
      }

      return +newValue.toFixed(decimalPrecision ? decimalPrecision : 2);
    };

    return this.props.type === 'number' ? (
      <StepsContainer style={{ height: this.props.height }}>
        <StepUp onClick={handleStepClick('up')}>
          <i className="fa fa-caret-up" />
        </StepUp>
        <StepDown onClick={handleStepClick('down')}>
          <i className="fa fa-caret-down" />
        </StepDown>
      </StepsContainer>
    ) : (
      ''
    );
  };

  constructor(props: InputProps) {
    super(props);

    this.id = props.id ? props.id : generateRandomInputId();
  }

  componentDidMount() {
    if (this.props.autofocus) this.input.focus();

    if (this.props.autoselect) this.input.select();
  }

  render() {
    const {
      autoComplete,
      className,
      dataAnalytics,
      dataE2E,
      dataPendo,
      dataTestid,
      disabled,
      height,
      inputMode,
      inputRef,
      invalid,
      labelPosition,
      max,
      min,
      name,
      onBlur,
      onFocus,
      onKeyDown,
      pasteEdited,
      placeholder,
      readOnly,
      responsive,
      size,
      step,
      tabIndex,
      type,
      value,
    } = this.props;

    const theme = {
      backgroundColor: pasteEdited ? colors.sea25 : null,
      borderColor: (invalid ? colors.dangerRed : colors.form.greyBorder) as string,
      height,
      responsive,
      width: size,
    };

    if (disabled) theme.borderColor = colors.form.greyDisabledBorder;

    const inputProps = {
      autoComplete,
      className,
      disabled,
      id: this.id,
      inputMode,
      max,
      min,
      name,
      onBlur,
      onChange: this.handleChange,
      onFocus,
      onKeyDown,
      placeholder,
      readOnly,
      ref: (elem: HTMLInputElement) => {
        this.input = elem;
        if (inputRef) {
          if (typeof inputRef === 'function') inputRef(elem);
          else inputRef.current = this.input;
        }
      },
      step,
      tabIndex,
      type,
      value,
    };

    let inputLabel;
    let inputElement;
    let inputWithLabel;
    let InputWithLabelContainer;

    // Set label and input element based on label position
    if (labelPosition === 'left') {
      inputLabel = this.renderInputLabel();
      inputElement = (
        <InputWithLeftMargin
          {...inputProps}
          data-analytics={dataAnalytics}
          data-e2e={dataE2E}
          data-pendo={dataPendo}
          data-testid={dataTestid}
        />
      );
      InputWithLabelContainer = FlexContainer;
    } else {
      inputLabel = (
        <LabelWithUrl>
          {this.renderInputLabel()}
          {this.renderLink()}
        </LabelWithUrl>
      );
      inputElement = (
        <StyledInput
          {...inputProps}
          data-analytics={dataAnalytics}
          data-e2e={dataE2E}
          data-pendo={dataPendo}
          data-testid={dataTestid}
        />
      );
      InputWithLabelContainer = React.Fragment;
    }

    inputWithLabel = (
      <InputWithLabelContainer>
        {inputLabel}
        <ThemeProvider theme={theme}>
          <FlexContainer>
            {inputElement}
            {this.renderNumberInputSteps()}
          </FlexContainer>
        </ThemeProvider>
      </InputWithLabelContainer>
    );

    return (
      <InputContainer className={this.props.wrapperClassName} type={type}>
        {inputWithLabel}
        {this.renderErrorMessage()}
      </InputContainer>
    );
  }

  renderErrorMessage() {
    const { errorMessage, errorMessageStyle } = this.props;

    if (!errorMessage) return null;

    return <ErrorMessage customStyle={errorMessageStyle}>{errorMessage}</ErrorMessage>;
  }

  renderInputLabel() {
    const { label, labelPosition, labelProps } = this.props;

    if (!label) return null;

    return (
      <Label htmlFor={this.id} labelPosition={labelPosition} {...labelProps}>
        {label}
      </Label>
    );
  }

  renderLink() {
    const { linkText, linkUrl } = this.props;
    if (!linkUrl || !linkText) return null;

    return (
      <GreyLink href={linkUrl} target="_blank">
        {linkText}
      </GreyLink>
    );
  }
}

interface InputFieldProps {
  focusedMessage?: string;
  hideErrors?: boolean;
}

export const InputField: React.FunctionComponent<
  InputFieldProps & InputProps & WrappedFieldProps
> = (props: InputFieldProps & InputProps & WrappedFieldProps) => {
  const {
    errorArea,
    errorMessageStyle,
    focusedMessage,
    hideErrors,
    infoMessage,
    input,
    meta,
    ...inputProps
  } = props;
  const invalid = meta && meta.touched && meta.error;
  const errors =
    !hideErrors && invalid ? (
      <FormErrorWithMarginTop customStyle={errorMessageStyle}>{meta.error}</FormErrorWithMarginTop>
    ) : null;
  const shouldShowFocusedMessage = focusedMessage && meta.active;
  const message =
    errorArea && !invalid ? (
      <MessageContainer>{infoMessage && <InfoMessage>{infoMessage}</InfoMessage>}</MessageContainer>
    ) : null;

  return (
    <div>
      <Input
        errorMessageStyle={errorMessageStyle}
        invalid={invalid}
        onBlur={input.onBlur}
        onChange={input.onChange}
        onFocus={input.onFocus}
        value={input.value}
        {...inputProps}
        data-e2e={inputProps.dataE2E}
        data-pendo={inputProps.dataPendo}
        data-testid={inputProps.dataTestid}
        name={inputProps.name}
      />
      {message}
      {errors}
      {shouldShowFocusedMessage && (
        <FormFieldFocusedMessage>{focusedMessage}</FormFieldFocusedMessage>
      )}
    </div>
  );
};

interface MaskedInputFieldProps {
  onClick?: () => void; // e.g. clears the field's value on click
}

export const MaskedInputField: React.FunctionComponent<
  InputFieldProps & InputProps & MaskedInputFieldProps & WrappedFieldProps
> = (props: InputFieldProps & InputProps & MaskedInputFieldProps & WrappedFieldProps) => {
  const { errorMessageStyle, hideErrors, input, meta, ...inputProps } = props;
  const invalid = meta && meta.touched && meta.error;
  const errors =
    !hideErrors && invalid ? (
      <FormErrorWithMarginTop customStyle={errorMessageStyle}>{meta.error}</FormErrorWithMarginTop>
    ) : null;

  const [isEditing, setIsEditing] = React.useState(false);

  const maskedTextOnClick = () => {
    if (props.onClick) props.onClick();

    setIsEditing(true);
  };

  const renderMaskedText = () => {
    const tooltipText =
      'This field is masked for security reasons. Click to edit it. Ignore it to keep the previous value.';
    return (
      <StyledMaskedText
        disabled={props.disabled}
        onClick={props.disabled ? undefined : maskedTextOnClick}
        tabIndex={0} // Added for tab accessibility
      >
        {input.value}{' '}
        <Tooltip placement="top" title={tooltipText}>
          <i className="fa fa-question-circle" />
        </Tooltip>
      </StyledMaskedText>
    );
  };

  const renderLabel = () => {
    const { label, labelProps } = props;

    if (!label) return null;

    return <Label {...labelProps}>{label}</Label>;
  };

  return isEditing ? (
    <div>
      <Input
        invalid={invalid}
        onChange={input.onChange}
        value={input.value}
        {...inputProps}
        data-e2e={inputProps.dataE2E}
        data-pendo={inputProps.dataPendo}
        data-testid={inputProps.dataTestid}
      />
      {errors}
    </div>
  ) : (
    <React.Fragment>
      {renderLabel()}
      {renderMaskedText()}
    </React.Fragment>
  );
};

export default Input;
